// Las dos puertas del alta en bloque por carpetas
// (ADR-2026-10-16-subir-varios-con-zip-y-carpetas): un .zip o una carpeta soltada
// en escritorio. Las dos terminan en `organizarPorCarpetas`, que tiene la regla.

import { unzip, type Unzipped, type UnzipFileInfo } from "fflate";
import {
  limitarProductos,
  mapearLote,
  organizarPorCarpetas,
  repararNombreDeZip,
  type EntradaDeLote,
  type LotePorCarpetas,
} from "./lotePorCarpetas";

/** El .zip se lee entero en memoria (y después sus fotos): en un teléfono, más que
 *  esto ya arriesga la pestaña. 20 productos × 5 fotos de cámara entran de sobra. */
export const MAX_TAMANO_ZIP = 300 * 1024 * 1024;
/** Una foto descomprimida: el mismo tope que una foto suelta (`BULK_MAX_FILE_SIZE`). */
export const MAX_FOTO_DESCOMPRIMIDA = 100 * 1024 * 1024;
/** Todo lo que se descomprime de un .zip: el tope del archivo mira los bytes COMPRIMIDOS,
 *  y un .zip chico puede expandirse a mucho más (review Greptile #184). */
export const MAX_TOTAL_DESCOMPRIMIDO = 600 * 1024 * 1024;

/** Error con un mensaje que se le puede mostrar tal cual al usuario. */
export class ErrorDeLote extends Error {}

export const esZip = (file: File): boolean =>
  /\.zip$/i.test(file.name) || /^application\/(x-)?zip(-compressed)?$/.test(file.type);

const MIME_POR_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
};

const nombreBase = (ruta: string) => ruta.slice(ruta.replace(/\\/g, "/").lastIndexOf("/") + 1);

/** Las fotos de un .zip no traen tipo: sin él, el preview y el upload no saben
 *  qué son. Se deduce de la extensión, la misma que filtró `organizarPorCarpetas`. */
const mimeDe = (ruta: string) =>
  MIME_POR_EXTENSION[ruta.slice(ruta.lastIndexOf(".") + 1).toLowerCase()] ?? "";

const descomprimir = (
  bytes: Uint8Array,
  filtro: (f: UnzipFileInfo) => boolean,
): Promise<Unzipped> =>
  new Promise((resolve, reject) =>
    unzip(bytes, { filter: filtro }, (err, datos) => (err ? reject(err) : resolve(datos))),
  );

/**
 * Lee un .zip y arma sus productos. Dos pasadas: la primera sólo lee los nombres
 * (el índice del .zip, sin descomprimir nada); la segunda descomprime nada más las
 * fotos que van a entrar (hasta `maxProductos` y 5 por carpeta). Un .zip con 300
 * carpetas no descomprime 1500 fotos para usar 100.
 */
export async function leerZipDeProductos(
  zip: File,
  maxProductos: number,
  // Inyectables sólo para los tests (armar un .zip de 100 MB en un test no tiene sentido).
  topes = { foto: MAX_FOTO_DESCOMPRIMIDA, total: MAX_TOTAL_DESCOMPRIMIDO },
): Promise<{ lote: LotePorCarpetas<File>; omitidos: number; pesadas: number }> {
  if (zip.size > MAX_TAMANO_ZIP) {
    throw new ErrorDeLote(
      `"${zip.name}" pesa más de ${MAX_TAMANO_ZIP / 1024 / 1024} MB. Partilo en varios .zip más chicos.`,
    );
  }
  const noAbre = new ErrorDeLote(
    `No pudimos abrir "${zip.name}". Si tiene contraseña, sacásela; si no, volvé a comprimirlo y probá de nuevo.`,
  );
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await zip.arrayBuffer());
  } catch {
    throw noAbre;
  }

  const nombres: string[] = [];
  // Lo que cada foto ocupa descomprimida, según el índice del .zip.
  const tamano = new Map<string, number>();
  try {
    await descomprimir(bytes, (f) => {
      nombres.push(f.name);
      tamano.set(f.name, f.originalSize);
      return false;
    });
  } catch {
    throw noAbre;
  }

  // El dato de cada foto es su nombre crudo: es la clave de `Unzipped`.
  const { lote, omitidos } = limitarProductos(
    organizarPorCarpetas(nombres.map((n) => ({ ruta: repararNombreDeZip(n), dato: n }))),
    maxProductos,
  );
  const elegidos = new Set(
    [...lote.carpetas.flatMap((c) => c.archivos), ...lote.sueltas].filter(
      (n) => (tamano.get(n) ?? 0) <= topes.foto,
    ),
  );
  const pesadas =
    lote.carpetas.reduce((n, c) => n + c.archivos.length, 0) + lote.sueltas.length - elegidos.size;
  if (elegidos.size === 0)
    return { lote: mapearLote<string, File>(lote, () => null), omitidos, pesadas };
  let total = 0;
  for (const n of elegidos) total += tamano.get(n) ?? 0;
  if (total > topes.total) {
    throw new ErrorDeLote(
      `Las fotos de "${zip.name}" ocupan más de ${Math.round(topes.total / 1024 / 1024)} MB descomprimidas. Partilo en varios .zip más chicos.`,
    );
  }

  let datos: Unzipped;
  try {
    datos = await descomprimir(bytes, (f) => elegidos.has(f.name));
  } catch {
    throw noAbre;
  }
  return {
    lote: mapearLote(lote, (crudo) => {
      const contenido = elegidos.has(crudo) ? datos[crudo] : undefined;
      if (!contenido) return null;
      const nombre = repararNombreDeZip(crudo);
      return new File([contenido], nombreBase(nombre), { type: mimeDe(nombre) });
    }),
    omitidos,
    pesadas,
  };
}

// ── Carpetas soltadas ─────────────────────────────────────────────────────

const leerDirectorio = (dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> =>
  new Promise((resolve, reject) => {
    const lector = dir.createReader();
    const todas: FileSystemEntry[] = [];
    // `readEntries` devuelve de a tandas (Chrome, de a 100): hay que llamarlo hasta
    // que venga vacío o se pierden archivos de las carpetas grandes.
    const tanda = () =>
      lector.readEntries((es) => {
        if (es.length === 0) resolve(todas);
        else {
          todas.push(...es);
          tanda();
        }
      }, reject);
    tanda();
  });

const leerArchivo = (entrada: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => entrada.file(resolve, reject));

async function recorrer(entrada: FileSystemEntry, salida: EntradaDeLote<File>[]) {
  // Ocultos y `__MACOSX` ni se recorren; `organizarPorCarpetas` igual los filtra.
  if (entrada.name.startsWith(".") || entrada.name === "__MACOSX") return;
  if (entrada.isFile) {
    try {
      salida.push({
        ruta: entrada.fullPath,
        dato: await leerArchivo(entrada as FileSystemFileEntry),
      });
    } catch {
      // Un archivo que el sistema no deja leer se saltea; el resto sigue.
    }
    return;
  }
  if (entrada.isDirectory) {
    for (const hija of await leerDirectorio(entrada as FileSystemDirectoryEntry)) {
      await recorrer(hija, salida);
    }
  }
}

/**
 * Si lo que se soltó trae al menos una carpeta, devuelve la función que la lee;
 * si son sólo archivos, null (el camino de siempre con `dataTransfer.files`).
 *
 * Se llama SINCRÓNICAMENTE dentro del `onDrop`: los `DataTransferItem` dejan de
 * valer después del primer `await`, por eso se capturan las entradas acá y la
 * lectura (asíncrona) queda para después.
 */
export function capturarCarpetasSoltadas(
  dt: DataTransfer,
): (() => Promise<EntradaDeLote<File>[]>) | null {
  const entradas = Array.from(dt.items ?? [])
    .filter((i) => i.kind === "file")
    .map((i) => i.webkitGetAsEntry?.() ?? null)
    .filter((e): e is FileSystemEntry => e !== null);
  if (!entradas.some((e) => e.isDirectory)) return null;
  return async () => {
    const salida: EntradaDeLote<File>[] = [];
    for (const e of entradas) await recorrer(e, salida);
    return salida;
  };
}
